import * as L from 'leaflet';
import { bucketIntoCells, clusterBubbleSpec } from '../models/cluster.model';

/**
 * COUNT-BUBBLE CLUSTERING — the Leaflet half, for any provider that draws
 * vessel markers on a Leaflet map (D10, 2026-08-13).
 *
 * The grid maths and the bubble markup are the pure model's
 * (`cluster.model.ts`); what lives here is everything Leaflet: projection, the
 * bubble pool, the layer, the show/hide of the hulls a bubble stood for.
 *
 * WHY THIS IS A SEPARATE FILE FROM `display.controller.ts`. It was that file's
 * private half until AISstream needed grouping too. `DisplayController` takes
 * an `AisMapContext` — the map, the ports, the selection — and the only object
 * that satisfies it is Voyage Radar's facade, so the pass was reachable by
 * exactly one provider. `AisstreamPlaybackController` owns a bare `L.Map`, a
 * `LayerGroup` and a `Map<mmsi, L.Marker>` and constructs none of that
 * machinery. This class therefore takes NO context: a way to reach the map, an
 * array of markers per pass, and four hooks for the decisions that genuinely
 * differ between providers.
 *
 * It is deliberately the FIRST module in `map-core/` written this way. The rest
 * are shared by location — they sit here because the refactor put map code
 * here, not because a second provider could call them. Two real implementers is
 * the bar this codebase sets for an abstraction (D8), and clustering is the
 * first thing to clear it.
 *
 * WHY `shared/leaflet/` AND NOT `map-core/`. It began as `map-core`'s file
 * and tripped rule R2 there — no map-core module may import a sibling, because
 * those six have mutual dependencies and reach each other through `ctx` ports
 * to stay acyclic. This class cannot participate in that cycle (it imports no
 * map-core module and takes no context), so the rule was arguably not aimed at
 * it — but an exemption you have to remember is a rule that gets broken, and
 * `grep "from './" shared/map-core/` staying at zero is worth more than one
 * fewer folder. `shared/leaflet/` is the honest name for what this is:
 * Leaflet rendering any provider can call, with no context to satisfy. It is
 * also where the next such extraction goes.
 *
 * Rules: no provider import, no `@angular/*`. Leaflet is allowed here and not
 * in `models/` (rule R1).
 */

/**
 * The provider-specific decisions. Every one is optional and the defaults are
 * the permissive reading — a provider that just wants bubbles passes `{}`.
 */
export interface ClusterLayerHooks {
  /**
   * May this marker be SHOWN OR HIDDEN by the pass at all?
   *
   * False means some earlier stage has already hidden it and owns that verdict,
   * so clustering must neither absorb it nor put it back. Voyage Radar's filter
   * pass writes `_vrVisible` for exactly this; a provider with no filter omits
   * the hook.
   */
  visible?(marker: any): boolean;
  /**
   * May this marker be SWALLOWED into a bubble?
   *
   * Distinct from `visible` on purpose, and the difference is one real rule: the
   * selected vessel is drawn but never absorbed — a selection that vanishes into
   * a count is a selection lost. Such a marker returns true from `visible` and
   * false from here, so it stays on the map outside every bubble.
   */
  absorbable?(marker: any): boolean;
  /**
   * Show or hide one vessel marker. Defaults to opacity + `display`, together:
   * opacity alone leaves an invisible marker still catching clicks.
   */
  hide?(marker: any, hidden: boolean): void;
  /**
   * The bubble was clicked but zooming cannot separate its members — vessels
   * moored side by side, or a map already at max zoom. Name the hulls instead.
   *
   * Omitted = such a click does nothing, which is the honest default for a
   * provider that has no list UI to open yet.
   */
  onIndivisible?(at: any, members: any[]): void;
}

/**
 * Marker flag recording that the last pass absorbed this hull into a bubble.
 *
 * Written on the marker rather than kept in a Set here because it is a
 * CROSS-MODULE contract, not bookkeeping: `SelectionController` reads
 * `_vrClustered !== true` to decide whether a vessel is really on screen. The
 * `_vr` prefix is historical (this began inside `VoyageMapController`) and is
 * kept rather than renamed — the name is load-bearing in a second file, and
 * churning it would buy nothing but a diff.
 */
const CLUSTERED = '_vrClustered';

export class ClusterLayer {
  /** Layer holding the count bubbles (null until the first one is needed). */
  private _layer: any = null;
  /**
   * Bubble markers, reused between passes rather than rebuilt.
   *
   * Playback runs this pass per frame, and a marker whose position and count are
   * unchanged should cost nothing — recreating a few hundred divIcons ten times a
   * second is exactly the stutter the label thinning was added to avoid.
   */
  private _pool: any[] = [];
  /** How many pooled bubbles the last pass actually used. */
  private _active = 0;

  /**
   * @param map    Reached through a thunk, not held: both providers re-attach to
   *               a new `L.Map` over their lifetime, and a stored reference
   *               would keep painting bubbles onto the old one.
   * @param hooks  See `ClusterLayerHooks`.
   */
  constructor(
    private readonly map: () => any,
    private readonly hooks: ClusterLayerHooks = {},
  ) {}

  private _visible(marker: any): boolean {
    return this.hooks.visible ? this.hooks.visible(marker) : true;
  }

  private _absorbable(marker: any): boolean {
    return this.hooks.absorbable ? this.hooks.absorbable(marker) : true;
  }

  private _hide(marker: any, hidden: boolean): void {
    if (this.hooks.hide) {
      this.hooks.hide(marker, hidden);
      return;
    }
    if (typeof marker.setOpacity === 'function') marker.setOpacity(hidden ? 0 : 1);
    const el = typeof marker.getElement === 'function' ? marker.getElement() : null;
    if (el) (el as HTMLElement).style.display = hidden ? 'none' : '';
  }

  /**
   * One clustering pass over `markers`.
   *
   * The grid is anchored to WORLD pixels (`map.project` at the current zoom), not
   * to the viewport. That distinction is the difference between clustering that
   * feels solid and clustering that shimmers: bucket by screen position and the
   * grid slides across the vessels as you pan, so counts flicker and bubbles jump
   * between cells under a drag that changed nothing. In world pixels the cells
   * are pinned to the sea floor — a pan cannot re-bucket anything, and only a
   * zoom (which genuinely changes what fits) redraws them.
   *
   * `markers` is passed per call rather than read through a hook because the
   * layer being clustered CHANGES: Voyage Radar clusters its live layer or the
   * playback frame's, whichever is current.
   *
   * @param on  Grouping switched on from the toolbar. Off is not a no-op — see
   *            below.
   */
  apply(on: boolean, markers: any[]): void {
    const map = this.map();
    if (!map) {
      this.clear();
      return;
    }

    // Switched off from the toolbar: every hull the visibility pass allowed goes
    // back on the map. Written explicitly rather than just dropping the bubbles —
    // the vessels a bubble stood for are hidden, and nothing else would restore
    // them.
    if (!on) {
      // Only when bubbles were actually up: with grouping off this runs on every
      // pan and on every playback frame, and there is nothing to put back once
      // the first pass has put it back.
      if (this._active) {
        for (const marker of markers) {
          marker[CLUSTERED] = false;
          if (!this._visible(marker)) continue;
          this._hide(marker, false);
        }
      }
      this.clear();
      return;
    }

    const zoom = map.getZoom();
    // Padded, like the labels: a bubble whose members sit just off screen still
    // belongs on the edge of the view, and the slack keeps a small pan from
    // rebuilding everything.
    const bounds = map.getBounds().pad(0.2);

    // Leaflet's half: which markers participate, and where they sit projected.
    // The grid maths on the result is the model's (`bucketIntoCells`).
    const points: Array<{ x: number; y: number; item: any }> = [];
    for (const marker of markers) {
      marker[CLUSTERED] = false;
      if (!this._visible(marker)) continue;
      if (!this._absorbable(marker)) continue;
      const latlng = typeof marker.getLatLng === 'function' ? marker.getLatLng() : null;
      if (!latlng || !bounds.contains(latlng)) continue;
      const pt = map.project(latlng, zoom);
      points.push({ x: pt.x, y: pt.y, item: marker });
    }

    let used = 0;
    for (const cell of bucketIntoCells<any>(points)) {
      for (const marker of cell.members) marker[CLUSTERED] = true;
      const centre = map.unproject(L.point(cell.centroidX, cell.centroidY), zoom);
      this._place(used++, centre, cell.members);
    }

    // Hulls the bubbles did not take are drawn; the ones they took are not. Both
    // written here, so a vessel released by a zoom comes straight back.
    for (const marker of markers) {
      if (!this._visible(marker)) continue;
      this._hide(marker, marker[CLUSTERED] === true);
    }
    this._trim(used);
  }

  /** Positions (and if needed creates) pooled bubble `index`. */
  private _place(index: number, at: any, members: any[]): void {
    const map = this.map();
    // Recreated only if a map reset swept it off — same guard as the track
    // renderer, and for the same reason: the reference outlives the sweep.
    if (!this._layer || !map?.hasLayer(this._layer)) {
      this._layer = L.layerGroup().addTo(map);
      this._pool = [];
      this._active = 0;
    }
    let bubble = this._pool[index];
    if (!bubble) {
      bubble = L.marker(at, {
        icon: this._icon(members.length),
        // Above the vessels it stands for, below the traced ghost (200000).
        zIndexOffset: 150000,
        keyboard: false,
      } as any);
      bubble.on('click', () => this._onClick(bubble));
      this._pool[index] = bubble;
    } else {
      bubble.setLatLng(at);
      // `setIcon` rebuilds the element, so only when the number on it changed —
      // during playback most bubbles keep their count from frame to frame.
      if (bubble._vrCount !== members.length) {
        bubble.setIcon(this._icon(members.length));
      }
    }
    bubble._vrCount = members.length;
    bubble._vrMembers = members;
    if (!this._layer.hasLayer(bubble)) this._layer.addLayer(bubble);
  }

  /** Drops pooled bubbles beyond the `used` this pass needed. */
  private _trim(used: number): void {
    for (let i = used; i < this._active; i++) {
      const bubble = this._pool[i];
      if (bubble && this._layer?.hasLayer(bubble)) {
        this._layer.removeLayer(bubble);
      }
    }
    this._active = used;
  }

  /** The bubble glyph — tiers and markup decided pure in `clusterBubbleSpec`. */
  private _icon(count: number): any {
    return L.divIcon(clusterBubbleSpec(count));
  }

  /**
   * Clicking a bubble frames what is inside it — which is the whole interaction,
   * so it has to work even when zooming cannot separate the members.
   *
   * Vessels moored side by side at a berth, or a map already at its deepest zoom,
   * would otherwise be a bubble that answers a click by doing nothing. Those go
   * to `onIndivisible` to be named instead.
   */
  private _onClick(bubble: any): void {
    const map = this.map();
    const members: any[] = bubble?._vrMembers ?? [];
    if (!map || !members.length) return;
    const latlngs = members
      .map((m) => (typeof m.getLatLng === 'function' ? m.getLatLng() : null))
      .filter(Boolean);
    if (!latlngs.length) return;
    const bounds = L.latLngBounds(latlngs as any);
    const zoom = map.getZoom();
    const maxZoom = map.getMaxZoom?.() ?? 18;
    // 30 m across is one ship's length: at that spread there is no zoom at which
    // these become separate arrows.
    const spread = bounds.isValid()
      ? map.distance(bounds.getNorthWest(), bounds.getSouthEast())
      : 0;
    if (zoom >= maxZoom || spread < 30) {
      this.hooks.onIndivisible?.(bubble.getLatLng(), members);
      return;
    }
    // Capped rather than a straight fit: a cell holding two tight knots of
    // traffic would otherwise jump straight to street zoom and lose the context
    // around it.
    map.fitBounds(bounds, {
      padding: [48, 48],
      maxZoom: Math.min(zoom + 3, maxZoom),
    } as any);
  }

  /** Takes every bubble off the map — the vessels they stood for have gone. */
  clear(): void {
    if (this._layer) this._layer.clearLayers();
    this._pool = [];
    this._active = 0;
  }

  /**
   * Forgets the bubble layer WITHOUT touching the map (invariant I1).
   *
   * For a task switch, which runs inside a sweep that has already taken every
   * layer off: `clear()` here would call `clearLayers()` on a group the map no
   * longer holds. The next pass builds a fresh layer of its own.
   *
   * The one place the difference from `clear()` matters, and it is not
   * cosmetic — this is the bug shape invariant I1 exists to name.
   */
  dropRefs(): void {
    this._layer = null;
    this._pool = [];
    this._active = 0;
  }

  /**
   * Takes the bubble layer OFF the map and forgets it — for a provider that
   * tears down its own layers rather than having them swept.
   *
   * The third of three teardowns, and they are genuinely different: `clear()`
   * empties a layer that stays on the map (the vessels changed), `dropRefs()`
   * forgets a layer the map has already lost (I1), and this one removes it.
   * Using `dropRefs()` where this is meant leaves an empty `LayerGroup` behind
   * on every teardown, because the next pass builds a fresh one.
   */
  detach(): void {
    const map = this.map();
    if (this._layer && map?.hasLayer?.(this._layer)) map.removeLayer(this._layer);
    this.dropRefs();
  }
}
